import { LngLat } from '@yandex/ymaps3-types';
import { YMapLocationRequest } from '@yandex/ymaps3-types/imperative/YMap';
import { Feature as ClustererFeature } from '@yandex/ymaps3-types/packages/clusterer';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  COMMON_LOCATION_PARAMS,
  DEFAULT_SELECT_POINT_ZOOM_VALUE,
  LOCATION
} from '../consts';
import { getBounds } from '../helpers/getBounds';
import { PointFeature } from '../types';

const buildLocation = (
  center?: LngLat,
  zoom?: number
): YMapLocationRequest => ({
  ...LOCATION,
  ...(center && { center }),
  ...(zoom != null && { zoom })
});

export type MapUpdateEvent = {
  location: { zoom: number };
  mapInAction: boolean;
};

/**
 * Хук для управления состоянием карты
 */
export function useMapController(center?: LngLat, zoom?: number) {
  const [location, setLocation] = useState<YMapLocationRequest>(() =>
    buildLocation(center, zoom)
  );

  const [selectedPoint, setSelectedPoint] = useState<PointFeature | null>(null);
  const selectedPointRef = useRef<PointFeature | null>(null);
  const zoomAtSelectionRef = useRef<number | null>(null);

  selectedPointRef.current = selectedPoint;

  useEffect(() => {
    setLocation((prevLocation) => ({
      ...prevLocation,
      ...(center && { center }),
      ...(zoom != null && { zoom })
    }));
  }, [center, zoom]);

  /**
   * Обработчик клика по маркеру
   */
  const handleMarkerClick = useCallback(
    (isMap: boolean) => (feature: PointFeature) => {
      zoomAtSelectionRef.current = DEFAULT_SELECT_POINT_ZOOM_VALUE;

      setLocation((prevLocation) => ({
        ...prevLocation,
        bounds: undefined,
        center: [
          feature.geometry.coordinates[0],
          feature.geometry.coordinates[1]
        ],
        zoom: DEFAULT_SELECT_POINT_ZOOM_VALUE,
        ...COMMON_LOCATION_PARAMS
      }));
      setSelectedPoint(feature);

      return { feature, isMap };
    },
    []
  );

  /**
   * Обработчик клика по кластеру
   */
  /**
   * Сброс выбранной точки при изменении zoom пользователем
   */
  const handleMapUpdate = useCallback((event: MapUpdateEvent) => {
    if (!selectedPointRef.current || event.mapInAction) {
      return;
    }

    const zoomAtOpen = zoomAtSelectionRef.current;

    if (zoomAtOpen == null) {
      return;
    }

    if (event.location.zoom !== zoomAtOpen) {
      setSelectedPoint(null);
      zoomAtSelectionRef.current = null;
    }
  }, []);

  const handleClusterClick = useCallback((features: ClustererFeature[]) => {
    zoomAtSelectionRef.current = null;
    setSelectedPoint(null);

    const bounds = getBounds(
      features.map((feature: ClustererFeature) => [
        feature.geometry.coordinates[0],
        feature.geometry.coordinates[1]
      ])
    );
    setLocation((prevLocation) => ({
      ...prevLocation,
      bounds,
      ...COMMON_LOCATION_PARAMS
    }));
  }, []);

  return {
    location,
    selectedPoint,
    setLocation,
    setSelectedPoint,
    handleMarkerClick,
    handleClusterClick,
    handleMapUpdate
  };
}
